import { useTranslation } from 'react-i18next';
import { trDisplay, formatDate } from '../../i18n';
import React, { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import SWRScenarioForm from './SWRScenarioForm';
import SWRConfigurationPanel from './SWRConfigurationPanel';
import { SWRHelp } from './SWRHelp';
import { SWRResultsSummary } from '../summary';
import SWRScenarioSavePanel from './SWRScenarioSavePanel';
import SWRSectionNav from './SWRSectionNav';
import SWRBackToTop from './SWRBackToTop';
import SWRRealWithdrawalMilestones from './SWRRealWithdrawalMilestones';
import StaleResultsBanner from '../ui/StaleResultsBanner';
import EmptyStateOverlay from '../ui/EmptyStateOverlay';
import { BarChart3, ListTree } from 'lucide-react';
import { formatCurrency } from '../../utils/formatters';
import { sameSWRFormInputs } from '../../lib/simulation/swrProvenance';

const SWRResultsContent = lazy(() => import('./SWRResultsContent'));
const SWRCSVExport = lazy(() => import('./SWRCSVExport'));

const SafeWithdrawalPage = ({
  results,
  isLoading,
  progress,
  hasRun,
  scenarioParams,
  lastRunParams,
  currentFormParams,
  safeWithdrawalIsLoading = false,
  safeWithdrawalProgress = null,
  safeWithdrawalMode,
  safeWithdrawalDetailedSimulations,
  onRunDetailedSafeWithdrawal,
  stressTestsIsLoading = false,
  stressTestsProgress = null,
  onScenarioSaved,
  onSubmit,
  onCancel,
  onParamsChange,
  restoredScenarioParams,
  restoreKey,
}) => {
  useTranslation();
  const [resultsViewMode, setResultsViewMode] = useState('essential');
  const [configurationOpen, setConfigurationOpen] = useState(true);
  const [configurationLevel, setConfigurationLevel] = useState('standard');
  useEffect(() => { if (restoreKey) setConfigurationOpen(true); }, [restoreKey]);
  const resultsSectionRef = useRef(null);
  const pageRef = useRef(null);
  const showCompleteResults = resultsViewMode === 'complete';
  const handleSubmit = params => {
    setConfigurationOpen(false);
    onSubmit(params);
    // Native/financial validation has already succeeded in the form.
    // Return to the top once, without moving again when background jobs finish.
    requestAnimationFrame(() => {
      const section = resultsSectionRef.current;
      if (!section) return;
      const headerHeight = document.querySelector('header')?.getBoundingClientRect().height || 0;
      const top = window.innerWidth < 1024
        ? Math.max(0, window.scrollY + section.getBoundingClientRect().top - headerHeight - 12) : 0;
      window.scrollTo({ top, behavior: 'instant' });
      section.focus({ preventScroll: true });
    });
  };
  const hasPendingChanges = useMemo(() => {
    if (!hasRun || !lastRunParams || !currentFormParams) return false;
    return !sameSWRFormInputs(lastRunParams, currentFormParams);
  }, [hasRun, lastRunParams, currentFormParams]);
  const sectionNavItems = useMemo(() => {
    if (!hasRun || !results) return [];

    return [
      { id: 'swr-save-scenario', label: 'Salva scenario' },
      { id: 'swr-results-summary', label: 'Riepilogo risultati' },
      ...(showCompleteResults && results?.summary?.guardrails ? [{ id: 'swr-withdrawal-plan', label: 'Piano di prelievo SWR' }] : []),
      ...(showCompleteResults && results?.macroSummary ? [{ id: 'swr-macro', label: 'Inflazione e liquidita' }] : []),
      ...(showCompleteResults && results?.summary?.dynamicCashBuffer ? [{ id: 'swr-cash-buffer', label: 'Dynamic Cash Buffer' }] : []),
      { id: 'swr-safe-withdrawal', label: 'Capitale necessario' },
      ...(results.capitalEvolution ? [{ id: 'swr-capital-evolution', label: 'Evoluzione capitale' }] : []),
      ...(results.perYearTableData ? [
        ...(results?.contributionAnalysis ? [{ id: 'swr-contributions', label: 'Apporti e continuità' }] : []),
        { id: 'swr-withdrawal-chart', label: 'Prelievo effettivo' },
        ...(showCompleteResults ? [{ id: 'swr-yearly-detail', label: 'Analisi dettagliata per anno' }] : []),
      ] : []),
      ...(results.depletionCumulativeByYear || results.depletionHistogram ? [{ id: 'swr-depletion-analysis', label: 'Esaurimento capitale' }] : []),
      ...(showCompleteResults && results.worst10Evolution && results.worst10Summary ? [{ id: 'swr-worst-case', label: 'Worst case scenario' }] : []),
      ...(results.worst10Evolution && results.worst10Summary ? [{ id: 'swr-stress-tests', label: 'Stress test' }] : []),
      ...(showCompleteResults && results.representativeSimulations?.length ? [{ id: 'swr-single-paths', label: 'Percorsi singoli' }] : []),
      { id: 'swr-export', label: 'Export dati' },
    ];
  }, [hasRun, results, showCompleteResults]);

  const finalCapitalPoint = results?.capitalEvolution?.[results.capitalEvolution.length - 1] || null;
  const activeMethodLabels = useMemo(() => {
    if (!results) return [];
    const labels = [];
    if (results.summary?.guardrails?.enabled) labels.push('Guardrail attivi');
    if (results.summary?.dynamicCashBuffer?.enabled) labels.push('Dynamic Cash Buffer');
    if (results.macroMode === 'stochastic-euro-v1') labels.push('Macro stocastica');
    if (results.returnParameterUncertainty?.enabled) labels.push('Incertezza parametri');
    return labels;
  }, [results]);

  return (
    <div ref={pageRef} tabIndex={-1} className="main-layout swr-layout swr-workspace focus:outline-none">
      <SWRBackToTop pageRef={pageRef} avoidRunAction />
      <SWRSectionNav sections={sectionNavItems} />

      <SWRConfigurationPanel triggerId="swr-main-status" open={configurationOpen} onOpenChange={setConfigurationOpen}
        level={configurationLevel} onLevelChange={setConfigurationLevel}>
        <div>
          {trDisplay(restoreKey > 0 && <p role="status" className="mb-3 text-sm text-blue-900">{trDisplay("Scenario caricato. Rivedi le ipotesi e avvia la simulazione per ottenere un nuovo risultato.")}</p>)}
          <SWRScenarioForm
            onSubmit={handleSubmit}
            onCancel={onCancel}
            hasBackgroundWork={safeWithdrawalIsLoading || stressTestsIsLoading}
            onParamsChange={onParamsChange}
            isLoading={isLoading}
            progress={progress}
            restoredScenarioParams={restoredScenarioParams}
            restoreKey={restoreKey}
            configurationLevel={configurationLevel}
            onConfigurationLevelChange={setConfigurationLevel}
            embedded
          />
        </div>
      </SWRConfigurationPanel>

      <section ref={resultsSectionRef} tabIndex={-1} aria-label={trDisplay('Risultati SWR')} className="results-section space-y-6 sm:space-y-8" data-testid="swr-results-section">
        <div className="flex flex-col gap-3 rounded-lg border border-slate-200 bg-white p-3 sm:flex-row sm:items-center sm:justify-between sm:p-4">
          <div>
            <p className="text-sm font-semibold text-slate-800 flex items-center gap-2">{trDisplay("Vista risultati")}<SWRHelp topic="resultsView" side="bottom" /></p>
            {trDisplay(results?.provenance?.units?.realMoneyBaseDate && <p className="text-xs text-slate-500">{trDisplay("Euro reali riferiti al ")}{formatDate(results.provenance.units.realMoneyBaseDate)}.</p>)}
            <p className="mt-0.5 text-xs text-slate-500">{trDisplay("Parti dalle decisioni principali oppure apri tutte le analisi disponibili.")}</p>
          </div>
          <div className="inline-flex rounded-md border border-slate-200 bg-slate-50 p-1" role="group" aria-label={trDisplay("Livello di dettaglio risultati SWR")}>
            <button
              type="button"
              onClick={() => setResultsViewMode('essential')}
              aria-pressed={!showCompleteResults}
              data-testid="swr-results-view-essential"
              className={`inline-flex min-h-9 items-center gap-1.5 rounded px-3 text-xs font-semibold transition-colors ${!showCompleteResults ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
            >
              <BarChart3 className="h-4 w-4" />{trDisplay("Essenziale")}</button>
            <button
              type="button"
              onClick={() => setResultsViewMode('complete')}
              aria-pressed={showCompleteResults}
              data-testid="swr-results-view-complete"
              className={`inline-flex min-h-9 items-center gap-1.5 rounded px-3 text-xs font-semibold transition-colors ${showCompleteResults ? 'bg-white text-indigo-700 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
            >
              <ListTree className="h-4 w-4" />{trDisplay("Analisi completa")}</button>
          </div>
        </div>

        {trDisplay(hasPendingChanges && <StaleResultsBanner variant="swr" />)}

        <div id="swr-save-scenario" className="scroll-mt-24">
          <SWRScenarioSavePanel
            results={results}
            scenarioParams={scenarioParams}
            hasRun={hasRun}
            onScenarioSaved={onScenarioSaved}
          />
        </div>

        <div id="swr-results-summary" className="relative scroll-mt-24">
          {trDisplay(!hasRun && <EmptyStateOverlay variant="swr" onConfigure={() => setConfigurationOpen(true)} />)}

          <div className={`animate-fadeIn ${!hasRun ? 'opacity-40 pointer-events-none' : ''}`}>
            <SWRResultsSummary
              summary={results?.summary}
              drawdownStats={results?.drawdownStats}
              isEmpty={!hasRun}
              detailMode={resultsViewMode}
            />
          </div>
        </div>

        {trDisplay(hasRun && results && (
          <div className="flex flex-wrap gap-3" data-testid="swr-essential-outcomes">
            <div className="min-w-0 flex-[1_1_10rem] rounded-lg border border-slate-200 bg-white px-4 py-3">
              <p className="text-xs text-slate-500">{trDisplay("Capitale finale mediano")}</p>
              <p className="mt-1 text-lg font-semibold text-slate-900">{trDisplay(formatCurrency(finalCapitalPoint?.median || 0))}</p>
            </div>
            <div className="min-w-0 flex-[1_1_10rem] rounded-lg border border-slate-200 bg-white px-4 py-3">
              <p className="text-xs text-slate-500">{trDisplay("Capitale finale 10° percentile")}</p>
              <p className="mt-1 text-lg font-semibold text-slate-900">{trDisplay(formatCurrency(finalCapitalPoint?.p10 || 0))}</p>
            </div>
            <div className="min-w-0 flex-[1_1_10rem] rounded-lg border border-slate-200 bg-white px-4 py-3">
              <p className="text-xs text-slate-500">{trDisplay("Metodologie applicate")}</p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {trDisplay((activeMethodLabels.length ? activeMethodLabels : ['Configurazione base']).map(label => (
                  <span key={label} className="rounded bg-slate-100 px-2 py-1 text-xs font-medium text-slate-700">{trDisplay(label)}</span>
                )))}
              </div>
            </div>
            <SWRRealWithdrawalMilestones data={results.perYearTableData} yearsToSimulate={results.yearsToSimulate} />
          </div>
        ))}

        {trDisplay(hasRun && results && (
          <Suspense fallback={(
            <div className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-500">{trDisplay("Caricamento dei risultati...")}</div>
          )}>
            <SWRResultsContent
              results={results}
              scenarioParams={scenarioParams}
              safeWithdrawalIsLoading={safeWithdrawalIsLoading}
              safeWithdrawalProgress={safeWithdrawalProgress}
              safeWithdrawalMode={safeWithdrawalMode}
              safeWithdrawalDetailedSimulations={safeWithdrawalDetailedSimulations}
              onRunDetailedSafeWithdrawal={onRunDetailedSafeWithdrawal}
              stressTestsIsLoading={stressTestsIsLoading}
              stressTestsProgress={stressTestsProgress}
              hasPendingChanges={hasPendingChanges}
              isLoading={isLoading}
              viewMode={resultsViewMode}
            />
          </Suspense>
        ))}
        {trDisplay((!hasRun || !results) && (
          <Suspense fallback={null}><SWRCSVExport compact /></Suspense>
        ))}
      </section>
    </div>
  );
};

export default SafeWithdrawalPage;
